import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";

import { NoteCard } from "@/components/activity-timeline/note-card";
import { Badge } from "@/components/ui/badge";
import { RecordNoteComposer } from "@/components/records/record-note-composer";
import { useAuth } from "@/contexts/auth-context";

import { useAddEntityNote, useDeleteEntityNote } from "./hooks";
import type { EntityNote, NoteTargetKind } from "./types";

function attachmentHref(note: EntityNote): string | null {
  if (note.attached_to.kind === "person") return `/customer/${note.attached_to.id}`;
  if (note.attached_to.kind === "location") return `/location/${note.attached_to.id}`;
  if (note.attached_to.kind === "deal") return `/deals/${note.attached_to.id}`;
  return null;
}

function attachmentCaption(note: EntityNote): string {
  const label = note.attached_to.label;
  if (note.attached_to.kind === "person") return label ? `On ${label}` : "On customer";
  if (note.attached_to.kind === "location") return label ? `On ${label}` : "On location";
  return label ? `On ${label}` : "On deal";
}

/** A customer, location or deal note, drawn as the timeline's note card (the deal and CCC trays' notes): a note
 *  attached to another record carries a tag linking to it. */
export function EntityNoteItem({
  note,
  pageKind,
  showDate = false,
}: {
  note: EntityNote;
  pageKind: NoteTargetKind;
  /** Outside a timeline with day headers, show the date with the time. */
  showDate?: boolean;
}) {
  const { user } = useAuth();
  const deleteNote = useDeleteEntityNote();
  const foreign = note.attached_to.kind !== pageKind;
  const canDelete = !!user && note.attached_to.kind !== "deal" && note.author_id === user.id;
  const href = foreign ? attachmentHref(note) : null;
  const tag = foreign ? (
    <Badge variant="secondary" className="rounded-full px-1.5 py-0 text-[10px] font-medium">
      {href ? (
        <Link to={href} className="inline-flex items-center gap-0.5 hover:underline" onClick={(e) => e.stopPropagation()}>
          {note.attached_to.kind === "location" ? <MapPin className="h-2.5 w-2.5" /> : null}
          {attachmentCaption(note)}
        </Link>
      ) : (
        attachmentCaption(note)
      )}
    </Badge>
  ) : null;

  return (
    <div data-testid="entity-note-item">
      <NoteCard
        note={note}
        tag={tag}
        canDelete={canDelete}
        deleteTestId="entity-note-delete"
        showDate={showDate}
        unknownAuthor={null}
        onDelete={{ pending: deleteNote.isPending, onClick: () => deleteNote.mutate({ noteId: note.id }) }}
      />
    </div>
  );
}

export function EntityNoteComposer({
  kind,
  targetId,
}: {
  kind: Exclude<NoteTargetKind, "deal">;
  targetId: string;
}) {
  const addNote = useAddEntityNote();
  return (
    <RecordNoteComposer
      testIdPrefix="entity-note"
      pending={addNote.isPending}
      onAdd={(body) => addNote.mutateAsync({ kind, targetId, body })}
    />
  );
}
